import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { ShowHide } from 'components/layout';
import { NonIdealState } from '@blueprintjs/core';
import { Button } from 'components/buttons';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import 'components/listing/Listing.css';

export function ExpandableListingTable({
    id = null,
    loadingData = true,
    classes = "",
    rowData = [],
    headers = [],
    noDataHeading = "Nothing to see here...",
    noDataMessage = "No data available",
    onRowClick = null,
    columnWidths = [],
    hasFixedHeight = false,
    fixedHeight = "400px",
    hideClaretIfNullContent = false
}) {
    const [expandedRows, setExpandedRows] = useState({});

    function noDataContent() {
        return (
            <Fragment>
                <div className="clear-divide"></div>
                <div className="list-table-no-results">
                    <NonIdealState
                        title={noDataHeading}
                        description={noDataMessage}
                    />
                </div>
            </Fragment>
        );
    }
    
    function renderHeaders() {
        var headerElements = headers.map((header, index) => {
            const headerId = uuidv4();
            const widthStyle = getWidthStyle(index);
            return (<div key={`expandable-listing-table-header-${headerId}`} className="expandable-listing-table-header" style={widthStyle}>{header}</div>);
        });

        return (
            <div className={classNames("expandable-listing-table-headers-container inline-items", { "locked-height": hasFixedHeight })} >{headerElements}</div>
        );
    }

    function renderRows() {
        var rowElements = rowData.map((row, rowIndex) => {
            const isRowExpanded = expandedRows[rowIndex] === true;
            const rowId = uuidv4();

            const columnElement = row.rowContent.map((column, colIndex) => {
                const columnId = uuidv4();
                const widthStyle = getWidthStyle(colIndex);

                return (
                    <div
                        key={`expandable-listing-table-row-${rowId}-cell-${columnId}`} 
                        className="expandable-listing-table-row-cell" 
                        style={widthStyle}
                    >
                        {column}
                    </div>
                );
            });

            return (
                <div 
                    role="presentation"
                    key={`expandable-listing-table-row-${rowId}`} 
                    className={classNames("expandable-listing-table-row", { "expanded": isRowExpanded })} 
                    onClick={(e) => handleRowClick(rowIndex, e)}
                >
                    <div className="expandable-listing-table-row-content inline-items">
                        {columnElement}
                        <ShowHide
                            evaluator={!hideClaretIfNullContent || row.rowExpandedContent}
                            show={(
                                <div className="expandable-listing-table-row-expand-button" key={`${rowId}-expand-button`} >
                                    <Button
                                        onClick={(e) => handleRowClick(rowIndex, e)}
                                        icon={isRowExpanded ? "caret-up" : "caret-down"}
                                        iconOnly={true}
                                        minimal={true}
                                        large={false}
                                    />
                                </div>
                            )}
                            hide={(
                                <div className="hidden-claret"></div>
                            )}
                        />
                    </div>
                    <ShowHide
                        evaluator={isRowExpanded}
                        show={(
                            <div key={`expandable-listing-table-row-expanded-content-${rowId}`} className="expandable-listing-table-row-expanded-content">
                                <ShowHide 
                                    evaluator={row.rowExpandedContent !== null}
                                    show={(
                                        <Fragment>{row.rowExpandedContent}</Fragment>
                                    )}
                                    hide={(
                                        <div className="expandable-listing-table-row-expanded-content-no-data">{noDataMessage}</div>
                                    )}
                                />
                            </div>
                        )}
                    />
                </div>
            );
        });

        return (
            <div className={classNames("expandable-listing-table-rows-container", { "locked-height": hasFixedHeight })} style={getHeightStyle()}>
                {rowElements}
            </div>
        );
    }

    function getHeightStyle() {
        return hasFixedHeight 
        ? { height: fixedHeight }
        : { };
    }

    function handleRowClick(index, event) {
        event.stopPropagation();
        toggleRowExpansion(index);

        if (onRowClick) {
            onRowClick(index);
        }
    }

    function toggleRowExpansion(index) {
        setExpandedRows((prev) => ({
            [index]: !prev[index]
        }));
    }

    function getWidthStyle(index) {
        var widthStyle = {};

        if (columnWidths && columnWidths.length >= index) {
            var columnWidth = columnWidths[index]

            widthStyle = columnWidth === null 
                ? { flex: 1 }
                : { maxWidth: columnWidth, minWidth: columnWidth }
        }

        return widthStyle;
    }

    return (
        <div id={id} className={classNames("expandable-listing-table", classes)}>
            <ShowHide
                evaluator={rowData && rowData.length === 0 && !loadingData}
                show={(
                    noDataContent()
                )}
                hide={(
                    <Fragment>
                        {renderHeaders()}
                        {renderRows()}
                    </Fragment>
                )}
            />
        </div>
    );
}

ExpandableListingTable.propTypes = {
    id: PropTypes.string,
    loadingData: PropTypes.bool.isRequired,
    classes: PropTypes.string,
    rowData: PropTypes.array.isRequired,
    headers: PropTypes.array,
    noDataHeading: PropTypes.string,
    noDataMessage: PropTypes.string,
    onRowClick: PropTypes.func,
    columnWidths: PropTypes.array,
    hasFixedHeight: PropTypes.bool,
    fixedHeight: PropTypes.string,
    hideClaretIfNullContent: PropTypes.bool
};